import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Copy, KeyRound, Loader2, RefreshCw, Trash2 } from "lucide-react";
import {
  type OpacApplication,
  createOpacApplication,
  createOpacApplicationSuperAdmin,
  fetchOpacApplications,
  fetchOpacApplicationsSuperAdmin,
  revokeOpacApplication,
  revokeOpacApplicationSuperAdmin,
  rotateOpacApplication,
  rotateOpacApplicationSuperAdmin,
} from "@/lib/api/opac-applications";

interface OpacPublicKeysSectionProps {
  tenantId?: string;
  selfServiceEnabled: boolean;
  publicOpacPath: string;
  superAdminMode?: boolean;
}

export function OpacPublicKeysSection({
  tenantId,
  selfServiceEnabled,
  publicOpacPath,
  superAdminMode = false,
}: OpacPublicKeysSectionProps) {
  const [applications, setApplications] = useState<OpacApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [origins, setOrigins] = useState("");
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const busy = pendingKey !== null;
  const [error, setError] = useState("");

  const loadApplications = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data =
        superAdminMode && tenantId
          ? await fetchOpacApplicationsSuperAdmin(tenantId)
          : await fetchOpacApplications();
      setApplications(data);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "Failed to load OPAC applications";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [superAdminMode, tenantId]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const parseOrigins = (value: string) =>
    value
      .split(/[\n,]/)
      .map((o) => o.trim())
      .filter(Boolean);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setPendingKey("create");
    setError("");
    try {
      const payload = {
        name: name.trim(),
        allowedOrigins: parseOrigins(origins),
      };
      const result =
        superAdminMode && tenantId
          ? await createOpacApplicationSuperAdmin(tenantId, payload)
          : await createOpacApplication(payload);
      setRevealedKey(result.apiKey);
      setName("");
      setOrigins("");
      await loadApplications();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "Failed to create OPAC application";
      setError(message);
    } finally {
      setPendingKey(null);
    }
  };

  const handleRotate = async (id: string) => {
    if (!confirm("Rotate this key? The previous key will stop working immediately.")) return;
    setPendingKey(`rotate:${id}`);
    setError("");
    try {
      const result =
        superAdminMode && tenantId
          ? await rotateOpacApplicationSuperAdmin(tenantId, id)
          : await rotateOpacApplication(id);
      setRevealedKey(result.apiKey);
      await loadApplications();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "Failed to rotate key";
      setError(message);
    } finally {
      setPendingKey(null);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!confirm("Revoke this OPAC application? External sites using this key will lose access.")) {
      return;
    }
    setPendingKey(`revoke:${id}`);
    setError("");
    try {
      if (superAdminMode && tenantId) {
        await revokeOpacApplicationSuperAdmin(tenantId, id);
      } else {
        await revokeOpacApplication(id);
      }
      await loadApplications();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "Failed to revoke application";
      setError(message);
    } finally {
      setPendingKey(null);
    }
  };

  const canManage = superAdminMode || selfServiceEnabled;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <KeyRound className="h-5 w-5" />
          Public OPAC API Keys
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Separate from integration API keys. Use these on external library websites (e.g.
          tasuedlibrary.org) to read catalog search, journals, digital resources, and availability.
        </p>
        <p className="text-xs text-gray-500 mt-2">
          Public API path: <code className="bg-gray-100 px-1 rounded">{publicOpacPath}</code>
        </p>
        <p className="text-xs mt-1">
          <a
            href="/documentation/public-opac"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Public OPAC integration guide (full documentation)
          </a>
        </p>
      </div>

      {!canManage && (
        <Card className="p-4 border-amber-200 bg-amber-50">
          <p className="text-sm text-amber-900">
            OPAC key management is handled by PhiloLMS for this library. Contact support to create
            or rotate keys.
          </p>
        </Card>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      {revealedKey && (
        <Card className="p-4 border-primary/30 bg-primary/5">
          <p className="text-sm font-medium text-gray-900 mb-2">
            Copy this key now — it won&apos;t be shown again
          </p>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-white border rounded px-2 py-1 flex-1 break-all">
              {revealedKey}
            </code>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigator.clipboard.writeText(revealedKey)}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <Button size="sm" variant="ghost" className="mt-2" onClick={() => setRevealedKey(null)}>
            Dismiss
          </Button>
        </Card>
      )}

      {canManage && (
        <Card className="p-4 space-y-3">
          <Label htmlFor="opac-app-name">Application name</Label>
          <Input
            id="opac-app-name"
            placeholder="e.g. TASUED public website"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Label htmlFor="opac-origins">Allowed origins (comma or newline)</Label>
          <Input
            id="opac-origins"
            placeholder="https://tasuedlibrary.org"
            value={origins}
            onChange={(e) => setOrigins(e.target.value)}
          />
          <Button onClick={handleCreate} disabled={busy || !name.trim()} className="gap-2">
            {pendingKey === "create" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Create OPAC application
          </Button>
        </Card>
      )}

      <div className="space-y-3">
        {loading ? (
          <p className="text-sm text-gray-500">Loading applications...</p>
        ) : applications.length === 0 ? (
          <p className="text-sm text-gray-500">No OPAC applications yet.</p>
        ) : (
          applications.map((app) => (
            <Card key={app.id} className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-gray-900">{app.name}</p>
                  <p className="text-xs text-gray-500 mt-1">Prefix: {app.keyPrefix}…</p>
                  {app.allowedOrigins?.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Origins: {app.allowedOrigins.join(", ")}
                    </p>
                  )}
                  <div className="flex gap-2 mt-2">
                    <Badge variant={app.isActive ? "default" : "secondary"}>
                      {app.isActive ? "Active" : "Revoked"}
                    </Badge>
                    <Badge variant="outline">{app.usageCount} requests</Badge>
                  </div>
                </div>
                {canManage && app.isActive && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => handleRotate(app.id)}
                    >
                      {pendingKey === `rotate:${app.id}` ? (
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-1 h-4 w-4" />
                      )}
                      Rotate
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => handleRevoke(app.id)}
                    >
                      {pendingKey === `revoke:${app.id}` ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
